import type { AdminShareEarning, ShareEarningKind, ShareLinkChannel } from '@pulsify/shared/dto'
import { Check, HandCoins, X } from 'lucide-react'
import {
  useAdminShareReview,
  useDecideShareEarning,
} from '~/entities/share-link/model/use-share-links'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { Button } from '~/shared/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/shared/ui/card'
import { Skeleton } from '~/shared/ui/skeleton'

const CHANNEL_LABELS: Record<ShareLinkChannel, () => string> = {
  general: m.shareChannelGeneral,
  instagram_bio: m.shareChannelInstagramBio,
  instagram_story: m.shareChannelInstagramStory,
  whatsapp: m.shareChannelWhatsapp,
  facebook: m.shareChannelFacebook,
  qr: m.shareChannelQr,
}

const KIND_LABELS: Record<ShareEarningKind, () => string> = {
  visit: m.shareKindVisit,
  interest: m.shareKindInterest,
  signup: m.shareKindSignup,
  check_in: m.shareKindCheckIn,
  plan: m.shareKindPlan,
}

const NOTE_LABELS: Record<string, () => string> = {
  network: m.shareReviewNoteNetwork,
  burst: m.shareReviewNoteBurst,
}

function noteLabel(note: string | null): string {
  if (!note) return '-'
  return NOTE_LABELS[note]?.() ?? note
}

function HeldRow({ row }: { row: AdminShareEarning }) {
  const decide = useDecideShareEarning()
  const locale = getLocale()
  return (
    <tr className="border-border border-b last:border-0">
      <td className="py-3 pr-4 pl-4 font-medium">{row.businessName}</td>
      <td className="py-3 pr-4">{row.targetTitle}</td>
      <td className="py-3 pr-4 whitespace-nowrap">
        {KIND_LABELS[row.kind]()} · {CHANNEL_LABELS[row.channel]()}
      </td>
      <td className="py-3 pr-4 text-right tabular-nums">{row.credits}</td>
      <td className="py-3 pr-4 text-muted-foreground">{noteLabel(row.note)}</td>
      <td className="py-3 pr-4 text-muted-foreground whitespace-nowrap">
        {new Date(row.createdAt).toLocaleString(locale, {
          dateStyle: 'short',
          timeStyle: 'short',
        })}
      </td>
      <td className="py-3 pr-4">
        <div className="flex justify-end gap-1">
          <Button
            variant="outline"
            size="sm"
            disabled={decide.isPending}
            onClick={() => decide.mutate({ id: row.id, decision: 'approve' })}
          >
            <Check className="mr-1.5 h-3.5 w-3.5" />
            {m.shareReviewApprove()}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={decide.isPending}
            onClick={() => decide.mutate({ id: row.id, decision: 'reject' })}
          >
            <X className="mr-1.5 h-3.5 w-3.5" />
            {m.shareReviewReject()}
          </Button>
        </div>
      </td>
    </tr>
  )
}

export function ShareCashbackPage() {
  const query = useAdminShareReview()
  const count = new Intl.NumberFormat(getLocale())

  return (
    <div className="px-4 sm:px-8 py-8 sm:py-16">
      <div className="space-y-8 mx-auto max-w-6xl">
        <header className="space-y-1.5">
          <h1 className="flex items-center gap-2.5 font-semibold text-foreground text-3xl tracking-tight">
            <HandCoins className="w-7 h-7" aria-hidden />
            {m.shareReviewTitle()}
          </h1>
          <p className="max-w-3xl text-muted-foreground text-sm">{m.shareReviewSubtitle()}</p>
        </header>

        <Card>
          <CardHeader>
            <CardTitle>{m.shareReviewHeldTitle()}</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {query.isLoading ? (
              <Skeleton className="m-6 h-40" />
            ) : !query.data || query.data.held.length === 0 ? (
              <p className="p-12 text-muted-foreground text-sm text-center">
                {m.shareReviewEmpty()}
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-border border-b text-muted-foreground text-xs text-left uppercase tracking-wide">
                    <th className="py-3 pr-4 pl-4 font-medium">{m.shareReviewColumnBusiness()}</th>
                    <th className="py-3 pr-4 font-medium">{m.shareReviewColumnTarget()}</th>
                    <th className="py-3 pr-4 font-medium">{m.shareReviewColumnStep()}</th>
                    <th className="py-3 pr-4 font-medium text-right">
                      {m.shareReviewColumnCredits()}
                    </th>
                    <th className="py-3 pr-4 font-medium">{m.shareReviewColumnReason()}</th>
                    <th className="py-3 pr-4 font-medium">{m.shareReviewColumnTime()}</th>
                    <th className="py-3 pr-4 font-medium">
                      <span className="sr-only">{m.shareReviewColumnDecision()}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {query.data.held.map((row) => (
                    <HeldRow key={row.id} row={row} />
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{m.shareReviewTopTitle()}</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {query.data ? (
              query.data.topEarners.length === 0 ? (
                <p className="p-12 text-muted-foreground text-sm text-center">
                  {m.shareReviewTopEmpty()}
                </p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-border border-b text-muted-foreground text-xs text-left uppercase tracking-wide">
                      <th className="py-3 pr-4 pl-4 font-medium">
                        {m.shareReviewColumnBusiness()}
                      </th>
                      <th className="py-3 pr-4 font-medium text-right">
                        {m.shareReviewColumnVisits()}
                      </th>
                      <th className="py-3 pr-4 font-medium text-right">
                        {m.shareReviewColumnCredits()}
                      </th>
                      <th className="py-3 pr-4 font-medium text-right">
                        {m.shareReviewColumnHeld()}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {query.data.topEarners.map((row) => (
                      <tr key={row.businessId} className="border-border border-b last:border-0">
                        <td className="py-3 pr-4 pl-4 font-medium">{row.businessName}</td>
                        <td className="py-3 pr-4 text-right tabular-nums">
                          {count.format(row.visits)}
                        </td>
                        <td className="py-3 pr-4 text-right tabular-nums">
                          {count.format(row.credits)}
                        </td>
                        <td className="py-3 pr-4 text-right tabular-nums">
                          {count.format(row.held)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            ) : (
              <Skeleton className="m-6 h-40" />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
